import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit2, Trash2, X, Check, AlertCircle, 
  Phone, Mail, User, Key, Briefcase, ExternalLink, Loader2,
  UserPlus, UserCheck, Calendar, Clock, MapPin, Send, Download,
  FileText, Filter, MessageSquare, ChevronDown, Star
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminStaffManagement = ({ token, language, contacts = [], appointments = [], onRefresh }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [periodFilter, setPeriodFilter] = useState('7'); // 7, 30, or 'all'
  const [showStaffNotesModal, setShowStaffNotesModal] = useState(false);
  const [selectedJobNotes, setSelectedJobNotes] = useState(null);
  const [workSessions, setWorkSessions] = useState([]); // NEW: Store work sessions
  
  const [formData, setFormData] = useState({
    staff_id: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    iban: '',
    hourly_rate: '',
    regular_rate: '15',
    deep_rate: '17.50'
  });

  const [instructionsForm, setInstructionsForm] = useState({
    jobId: '',
    jobType: 'contact',
    instructions: ''
  });
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  
  // Get date filter based on period
  const getDateFilter = () => {
    const now = new Date();
    if (periodFilter === '7') {
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (periodFilter === '30') {
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    return null; // 'all' - no filter
  };
  
  // Export payroll as PDF
  const exportPayrollPDF = () => {
    const dateFilterStart = getDateFilter();
    const periodLabel = periodFilter === '7' ? (language === 'pt' ? 'Últimos 7 dias' : 'Last 7 days') :
                        periodFilter === '30' ? (language === 'pt' ? 'Últimos 30 dias' : 'Last 30 days') :
                        (language === 'pt' ? 'Todo o período' : 'All time');
    
    // Calculate data for each staff member
    const staffPayrollData = staff.map(member => {
      const memberAppointments = appointments.filter(a => a.assigned_staff_id === member.id);
      let completedJobs = memberAppointments.filter(a => a.status === 'completed');
      
      // Apply date filter
      if (dateFilterStart) {
        completedJobs = completedJobs.filter(a => {
          const apptDate = new Date(a.date || a.created_at);
          return apptDate >= dateFilterStart;
        });
      }
      
      // Service categorization - use member's individual rates
      const getServiceCategory = (serviceType) => {
        const type = (serviceType || '').toLowerCase();
        if (type.includes('deep') || type.includes('profunda')) return 'deep';
        if (type.includes('tenancy') || type.includes('arrendamento')) return 'endoftenancy';
        if (type.includes('office') || type.includes('escritório') || type.includes('escritorio')) return 'office';
        if (type.includes('upholstery') || type.includes('estofado')) return 'upholstery';
        return 'regular';
      };
      
      // Use individual staff rates instead of hardcoded values
      const memberRegularRate = member.regular_rate || 15;
      const memberDeepRate = member.deep_rate || 17.50;
      const serviceRates = { 
        regular: memberRegularRate, 
        office: memberRegularRate, 
        deep: memberDeepRate, 
        endoftenancy: memberDeepRate, 
        upholstery: 0 
      };
      const defaultHours = { regular: 3, office: 3, deep: 4, endoftenancy: 5, upholstery: 2 };
      
      const getEstimatedHours = (job) => {
        if (job.estimated_time) {
          const match = job.estimated_time.match(/(\d+)h\s*(\d*)m?/);
          if (match) return parseInt(match[1]) + (parseInt(match[2]) || 0) / 60;
        }
        return defaultHours[getServiceCategory(job.service_type)] || 3;
      };
      
      const getJobPayment = (job) => {
        const category = getServiceCategory(job.service_type);
        const hours = getEstimatedHours(job);
        if (category === 'upholstery') return job.custom_staff_payment || job.estimated_price * 0.5 || 0;
        return hours * serviceRates[category];
      };
      
      // Group by service type
      const breakdown = {
        regular: { count: 0, hours: 0, payment: 0 },
        office: { count: 0, hours: 0, payment: 0 },
        deep: { count: 0, hours: 0, payment: 0 },
        endoftenancy: { count: 0, hours: 0, payment: 0 },
        upholstery: { count: 0, hours: 0, payment: 0 }
      };
      
      completedJobs.forEach(job => {
        const category = getServiceCategory(job.service_type);
        breakdown[category].count++;
        breakdown[category].hours += getEstimatedHours(job);
        breakdown[category].payment += getJobPayment(job);
      });
      
      const totalHours = Object.values(breakdown).reduce((sum, b) => sum + b.hours, 0);
      const totalPayment = Object.values(breakdown).reduce((sum, b) => sum + b.payment, 0);
      
      return { member, breakdown, totalHours, totalPayment, jobCount: completedJobs.length };
    });
    
    const grandTotal = staffPayrollData.reduce((sum, s) => sum + s.totalPayment, 0);
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sancur - ${language === 'pt' ? 'Folha de Pagamento' : 'Payroll'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { color: #1a5c3a; border-bottom: 2px solid #1a5c3a; padding-bottom: 10px; margin-bottom: 5px; }
          .period { color: #666; margin-bottom: 20px; }
          .staff-card { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 8px; page-break-inside: avoid; }
          .staff-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee; padding-bottom: 10px; margin-bottom: 10px; }
          .staff-name { font-size: 16px; font-weight: bold; color: #333; }
          .staff-iban { font-size: 11px; color: #666; }
          .service-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-bottom: 10px; }
          .service-box { background: #f5f5f5; padding: 8px; border-radius: 4px; text-align: center; }
          .service-box.regular { background: #dbeafe; }
          .service-box.office { background: #cffafe; }
          .service-box.deep { background: #f3e8ff; }
          .service-box.endoftenancy { background: #e0e7ff; }
          .service-box.upholstery { background: #fce7f3; }
          .service-label { font-size: 10px; color: #666; }
          .service-value { font-size: 14px; font-weight: bold; }
          .service-hours { font-size: 10px; color: #888; }
          .total-row { display: flex; justify-content: space-between; background: #1a5c3a; color: white; padding: 10px; border-radius: 4px; }
          .grand-total { margin-top: 20px; padding: 15px; background: #1a5c3a; color: white; border-radius: 8px; text-align: center; }
          .grand-total h2 { margin: 0; font-size: 24px; }
          .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #999; }
          @media print { .staff-card { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>💰 ${language === 'pt' ? 'Folha de Pagamento - Sancur' : 'Payroll Report - Sancur'}</h1>
        <p class="period">${periodLabel} | ${language === 'pt' ? 'Gerado em' : 'Generated on'}: ${new Date().toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        
        ${staffPayrollData.map(({ member, breakdown, totalHours, totalPayment, jobCount }) => `
          <div class="staff-card">
            <div class="staff-header">
              <div>
                <div class="staff-name">👤 ${member.name}</div>
                <div class="staff-iban">IBAN: ${member.iban || 'N/A'}</div>
              </div>
              <div style="text-align: right;">
                <div style="font-size: 20px; font-weight: bold; color: #1a5c3a;">€${totalPayment.toFixed(2)}</div>
                <div style="font-size: 11px; color: #666;">${jobCount} ${language === 'pt' ? 'serviços' : 'services'} | ${totalHours.toFixed(1)}h</div>
              </div>
            </div>
            
            <div class="service-grid">
              <div class="service-box regular">
                <div class="service-label">Regular (€15/h)</div>
                <div class="service-value">${breakdown.regular.count}</div>
                <div class="service-hours">${breakdown.regular.hours.toFixed(1)}h = €${breakdown.regular.payment.toFixed(0)}</div>
              </div>
              <div class="service-box office">
                <div class="service-label">${language === 'pt' ? 'Escritório' : 'Office'} (€15/h)</div>
                <div class="service-value">${breakdown.office.count}</div>
                <div class="service-hours">${breakdown.office.hours.toFixed(1)}h = €${breakdown.office.payment.toFixed(0)}</div>
              </div>
              <div class="service-box deep">
                <div class="service-label">Deep (€17.50/h)</div>
                <div class="service-value">${breakdown.deep.count}</div>
                <div class="service-hours">${breakdown.deep.hours.toFixed(1)}h = €${breakdown.deep.payment.toFixed(0)}</div>
              </div>
              <div class="service-box endoftenancy">
                <div class="service-label">${language === 'pt' ? 'Fim Arrend.' : 'End Tenancy'} (€17.50/h)</div>
                <div class="service-value">${breakdown.endoftenancy.count}</div>
                <div class="service-hours">${breakdown.endoftenancy.hours.toFixed(1)}h = €${breakdown.endoftenancy.payment.toFixed(0)}</div>
              </div>
              <div class="service-box upholstery">
                <div class="service-label">${language === 'pt' ? 'Estofados' : 'Upholstery'}</div>
                <div class="service-value">${breakdown.upholstery.count}</div>
                <div class="service-hours">€${breakdown.upholstery.payment.toFixed(0)}</div>
              </div>
            </div>
          </div>
        `).join('')}
        
        <div class="grand-total">
          <h2>💵 ${language === 'pt' ? 'TOTAL A PAGAR' : 'TOTAL TO PAY'}: €${grandTotal.toFixed(2)}</h2>
        </div>
        
        <div class="footer">
          Sancur Property Services | Dublin, Ireland | www.sancur.ie
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    fetchStaff();
    fetchWorkSessions();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data.staff || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError(language === 'pt' ? 'Erro ao carregar funcionários' : 'Error loading staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/work-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Error fetching work sessions:', err);
    }
  };

  // Get real hours worked by a staff member based on work sessions
  const getStaffRealHours = (staffId, periodDays = null) => {
    const now = new Date();
    const filteredSessions = workSessions.filter(session => {
      if (session.staff_id !== staffId) return false;
      if (session.action !== 'finish') return false;
      
      if (periodDays) {
        const sessionDate = new Date(session.timestamp);
        const diffDays = (now - sessionDate) / (1000 * 60 * 60 * 24);
        if (diffDays > periodDays) return false;
      }
      
      return true;
    });
    
    const totalSeconds = filteredSessions.reduce((sum, s) => sum + (s.elapsed_seconds || 0), 0);
    return totalSeconds / 3600; // Convert to hours
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const payload = {
        ...formData,
        regular_rate: parseFloat(formData.regular_rate) || 15,
        deep_rate: parseFloat(formData.deep_rate) || 17.50
      };
      await axios.post(`${API}/admin/staff`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(language === 'pt' ? 'Funcionário criado com sucesso!' : 'Staff created successfully!');
      setShowCreateModal(false);
      setFormData({ staff_id: '', name: '', email: '', phone: '', password: '', iban: '', hourly_rate: '', regular_rate: '15', deep_rate: '17.50' });
      fetchStaff();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao criar funcionário' : 'Error creating staff'));
    }
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const updateData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.email) updateData.email = formData.email;
      if (formData.phone) updateData.phone = formData.phone;
      if (formData.password) updateData.password = formData.password;
      if (formData.iban) updateData.iban = formData.iban;
      if (formData.regular_rate) updateData.regular_rate = parseFloat(formData.regular_rate);
      if (formData.deep_rate) updateData.deep_rate = parseFloat(formData.deep_rate);
      
      await axios.patch(
        `${API}/admin/staff/${selectedStaff.id}?${new URLSearchParams(updateData)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(language === 'pt' ? 'Funcionário atualizado!' : 'Staff updated!');
      setShowEditModal(false);
      setSelectedStaff(null);
      fetchStaff();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao atualizar' : 'Error updating'));
    }
  };

  const handleDeleteStaff = async (staffMember) => {
    if (!window.confirm(language === 'pt' 
      ? `Tem certeza que deseja remover ${staffMember.name}?` 
      : `Are you sure you want to remove ${staffMember.name}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/staff/${staffMember.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(language === 'pt' ? 'Funcionário removido!' : 'Staff removed!');
      fetchStaff();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao remover' : 'Error removing'));
    }
  };

  const handleAssignJob = async () => {
    if (!selectedJob || !selectedStaff) return;
    
    try {
      await axios.post(
        `${API}/admin/jobs/assign?staff_internal_id=${selectedStaff.id}`,
        { job_id: selectedJob.id, job_type: selectedJob.type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(language === 'pt' 
        ? `Trabalho atribuído a ${selectedStaff.name}!` 
        : `Job assigned to ${selectedStaff.name}!`);
      setShowAssignModal(false);
      setSelectedJob(null);
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao atribuir' : 'Error assigning'));
    }
  };

  const handleSaveInstructions = async () => {
    try {
      await axios.patch(
        `${API}/admin/jobs/${instructionsForm.jobId}/instructions?job_type=${instructionsForm.jobType}`,
        { staff_instructions: instructionsForm.instructions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(language === 'pt' ? 'Instruções salvas!' : 'Instructions saved!');
      setShowInstructionsModal(false);
      setInstructionsForm({ jobId: '', jobType: 'contact', instructions: '' });
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao salvar instruções' : 'Error saving instructions'));
    }
  };

  const openEditModal = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      staff_id: staffMember.staff_id,
      name: staffMember.name,
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      password: '',
      iban: staffMember.iban || '',
      regular_rate: staffMember.regular_rate || '15',
      deep_rate: staffMember.deep_rate || '17.50'
    });
    setShowEditModal(true);
  };

  const openAssignModal = (job, type) => {
    setSelectedJob({ ...job, type });
    // Auto-select first staff if only one exists
    if (staff.length === 1) {
      setSelectedStaff(staff[0]);
    } else {
      setSelectedStaff(null);
    }
    setShowAssignModal(true);
  };

  const openInstructionsModal = (job, type) => {
    setInstructionsForm({
      jobId: job.id,
      jobType: type,
      instructions: job.staff_instructions || ''
    });
    setShowInstructionsModal(true);
  };

  // Get unassigned jobs (ONLY appointments - not quote requests)
  // Staff should only be assigned to accepted appointments, not pending quotes
  // Jobs with no staff at all
  const unassignedJobs = appointments
    .filter(a => !a.assigned_staff_id && (!a.assigned_staff_ids || a.assigned_staff_ids.length === 0))
    .map(a => ({ ...a, type: 'appointment' }));

  // Get assigned jobs (ONLY appointments) - jobs with at least one staff
  const assignedJobs = appointments
    .filter(a => a.assigned_staff_id || (a.assigned_staff_ids && a.assigned_staff_ids.length > 0))
    .map(a => ({ ...a, type: 'appointment' }));

  const getStaffName = (staffId) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember?.name || 'Unknown';
  };

  // Get all staff names for a job
  const getJobStaffNames = (job) => {
    if (job.assigned_staff_names && job.assigned_staff_names.length > 0) {
      return job.assigned_staff_names;
    }
    if (job.assigned_staff_ids && job.assigned_staff_ids.length > 0) {
      return job.assigned_staff_ids.map(id => getStaffName(id));
    }
    if (job.assigned_staff_name) {
      return [job.assigned_staff_name];
    }
    return [];
  };

  // Check if a staff member is already assigned to a job
  const isStaffAssigned = (job, staffId) => {
    if (job.assigned_staff_ids && job.assigned_staff_ids.includes(staffId)) return true;
    if (job.assigned_staff_id === staffId) return true;
    return false;
  };

  // Remove staff from job
  const handleRemoveStaff = async (job, staffId) => {
    try {
      await axios.delete(
        `${API}/admin/jobs/${job.id}/staff/${staffId}?job_type=${job.type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(language === 'pt' ? 'Staff removido do trabalho!' : 'Staff removed from job!');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao remover' : 'Error removing'));
    }
  };

  // Set team leader for a job
  const handleSetTeamLeader = async (job, staffId) => {
    try {
      await axios.patch(
        `${API}/admin/jobs/${job.id}/team-leader?staff_id=${staffId}&job_type=${job.type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(language === 'pt' ? 'Team Leader definido!' : 'Team Leader set!');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao definir líder' : 'Error setting leader'));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="staff-management">
      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <Check size={20} />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={20} />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {language === 'pt' ? 'Gestão de Funcionários' : 'Staff Management'}
          </h2>
          <p className="text-sm text-gray-500">
            {language === 'pt' 
              ? 'Crie contas, atribua trabalhos e gerencie a sua equipa'
              : 'Create accounts, assign jobs and manage your team'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Period Filter */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setPeriodFilter('7')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                periodFilter === '7' ? 'bg-white shadow text-primary font-medium' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              7 {language === 'pt' ? 'dias' : 'days'}
            </button>
            <button
              onClick={() => setPeriodFilter('30')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                periodFilter === '30' ? 'bg-white shadow text-primary font-medium' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              30 {language === 'pt' ? 'dias' : 'days'}
            </button>
            <button
              onClick={() => setPeriodFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                periodFilter === 'all' ? 'bg-white shadow text-primary font-medium' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {language === 'pt' ? 'Tudo' : 'All'}
            </button>
          </div>
          
          {/* Export Payroll Button */}
          <button
            onClick={exportPayrollPDF}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
            data-testid="export-payroll-btn"
          >
            <Download size={16} />
            {language === 'pt' ? 'Exportar Pagamentos' : 'Export Payroll'}
          </button>
          
          {/* New Staff Button */}
          <button
            onClick={() => {
              setFormData({ staff_id: '', name: '', email: '', phone: '', password: '' });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            data-testid="create-staff-btn"
          >
            <UserPlus size={20} />
            {language === 'pt' ? 'Novo Funcionário' : 'New Staff'}
          </button>
        </div>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-xl border border-gray-200 mb-8">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users size={20} className="text-primary" />
            {language === 'pt' ? 'Equipa' : 'Team'} ({staff.length})
          </h3>
        </div>
        
        {staff.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{language === 'pt' ? 'Nenhum funcionário registado' : 'No staff registered'}</p>
            <p className="text-sm mt-2">
              {language === 'pt' 
                ? 'Clique em "Novo Funcionário" para adicionar'
                : 'Click "New Staff" to add one'}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {staff.map((member) => {
              // Get all appointments for this staff member
              const memberAppointments = appointments.filter(a => a.assigned_staff_id === member.id);
              const completedJobs = memberAppointments.filter(a => a.status === 'completed');
              
              // Apply period filter
              const dateFilterStart = getDateFilter();
              const filteredCompletedJobs = dateFilterStart 
                ? completedJobs.filter(a => {
                    const apptDate = new Date(a.date || a.created_at);
                    return apptDate >= dateFilterStart;
                  })
                : completedJobs;
              
              // Service type detection
              const getServiceCategory = (serviceType) => {
                const type = (serviceType || '').toLowerCase();
                if (type.includes('deep') || type.includes('profunda')) return 'deep';
                if (type.includes('tenancy') || type.includes('arrendamento')) return 'endoftenancy';
                if (type.includes('office') || type.includes('escritório') || type.includes('escritorio')) return 'office';
                if (type.includes('upholstery') || type.includes('estofado')) return 'upholstery';
                return 'regular';
              };
              
              // Use individual staff rates instead of hardcoded values
              const memberRegularRate = member.regular_rate || 15;
              const memberDeepRate = member.deep_rate || 17.50;
              const serviceRates = {
                regular: memberRegularRate,
                office: memberRegularRate,
                deep: memberDeepRate,
                endoftenancy: memberDeepRate,
                upholstery: 0 // Custom - will use job price
              };
              
              // Time estimates per service type
              const getEstimatedHours = (job) => {
                if (job.estimated_time) {
                  const match = job.estimated_time.match(/(\d+)h\s*(\d*)m?/);
                  if (match) {
                    const hours = parseInt(match[1]) || 0;
                    const mins = parseInt(match[2]) || 0;
                    return hours + (mins / 60);
                  }
                }
                const category = getServiceCategory(job.service_type);
                const defaultHours = { regular: 3, office: 3, deep: 4, endoftenancy: 5, upholstery: 2 };
                return defaultHours[category] || 3;
              };
              
              // Calculate payment for a job
              const getJobPayment = (job) => {
                const category = getServiceCategory(job.service_type);
                const hours = getEstimatedHours(job);
                if (category === 'upholstery') {
                  return job.custom_staff_payment || job.estimated_price * 0.5 || 0;
                }
                return hours * serviceRates[category];
              };
              
              // Group jobs by service type for this week
              const jobsByType = {
                regular: filteredCompletedJobs.filter(j => getServiceCategory(j.service_type) === 'regular'),
                office: filteredCompletedJobs.filter(j => getServiceCategory(j.service_type) === 'office'),
                deep: filteredCompletedJobs.filter(j => getServiceCategory(j.service_type) === 'deep'),
                endoftenancy: filteredCompletedJobs.filter(j => getServiceCategory(j.service_type) === 'endoftenancy'),
                upholstery: filteredCompletedJobs.filter(j => getServiceCategory(j.service_type) === 'upholstery')
              };
              
              // Calculate hours and payment by type
              const hoursByType = {};
              const paymentByType = {};
              Object.keys(jobsByType).forEach(type => {
                hoursByType[type] = jobsByType[type].reduce((sum, j) => sum + getEstimatedHours(j), 0);
                paymentByType[type] = jobsByType[type].reduce((sum, j) => sum + getJobPayment(j), 0);
              });
              
              const totalHoursWeek = Object.values(hoursByType).reduce((a, b) => a + b, 0);
              const totalPaymentWeek = Object.values(paymentByType).reduce((a, b) => a + b, 0);
              
              // Get REAL hours from work sessions for this staff member
              const periodDays = periodFilter === 'all' ? null : parseInt(periodFilter);
              const realHoursWorked = getStaffRealHours(member.id, periodDays);
              const realPayment = realHoursWorked * memberRegularRate; // Use average rate
              
              // All-time breakdown
              const allTimeByType = {
                regular: completedJobs.filter(j => getServiceCategory(j.service_type) === 'regular'),
                office: completedJobs.filter(j => getServiceCategory(j.service_type) === 'office'),
                deep: completedJobs.filter(j => getServiceCategory(j.service_type) === 'deep'),
                endoftenancy: completedJobs.filter(j => getServiceCategory(j.service_type) === 'endoftenancy'),
                upholstery: completedJobs.filter(j => getServiceCategory(j.service_type) === 'upholstery')
              };
              
              // Service type display config
              const serviceConfig = {
                regular: { label: 'Regular', labelPt: 'Regular', color: 'blue', rate: `€${memberRegularRate.toFixed(2)}/h` },
                office: { label: 'Office', labelPt: 'Escritório', color: 'cyan', rate: `€${memberRegularRate.toFixed(2)}/h` },
                deep: { label: 'Deep Clean', labelPt: 'Deep Clean', color: 'purple', rate: `€${memberDeepRate.toFixed(2)}/h` },
                endoftenancy: { label: 'End Tenancy', labelPt: 'Fim Arrend.', color: 'indigo', rate: `€${memberDeepRate.toFixed(2)}/h` },
                upholstery: { label: 'Upholstery', labelPt: 'Estofados', color: 'pink', rate: 'Custom' }
              };
              
              const colorClasses = {
                blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', dot: 'bg-blue-500' },
                cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-600', dot: 'bg-cyan-500' },
                purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', dot: 'bg-purple-500' },
                indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-600', dot: 'bg-indigo-500' },
                pink: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-600', dot: 'bg-pink-500' }
              };
              
              return (
                <div key={member.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                        <User size={28} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-lg">{member.name}</h4>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Key size={14} />
                            {member.staff_id}
                          </span>
                          {member.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {member.phone}
                            </span>
                          )}
                          {member.email && (
                            <span className="flex items-center gap-1">
                              <Mail size={14} />
                              {member.email}
                            </span>
                          )}
                        </div>
                        {/* Individual Rates Display */}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                            Regular: €{(member.regular_rate || 15).toFixed(2)}/h
                          </span>
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                            Deep: €{(member.deep_rate || 17.50).toFixed(2)}/h
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href="/staff/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title={language === 'pt' ? 'Ver Portal Staff' : 'View Staff Portal'}
                      >
                        <ExternalLink size={18} />
                      </a>
                      <button
                        onClick={() => openEditModal(member)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title={language === 'pt' ? 'Editar' : 'Edit'}
                        data-testid={`edit-staff-${member.staff_id}`}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteStaff(member)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={language === 'pt' ? 'Remover' : 'Remove'}
                        data-testid={`delete-staff-${member.staff_id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Service Type Breakdown - This Week */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Clock size={16} className="text-blue-600" />
                      {language === 'pt' ? 'Resumo Esta Semana' : 'This Week Summary'}
                    </h5>
                    
                    {/* Service breakdown grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                      {Object.entries(serviceConfig).map(([type, config]) => {
                        const colors = colorClasses[config.color];
                        const jobs = jobsByType[type];
                        const hours = hoursByType[type];
                        const payment = paymentByType[type];
                        
                        return (
                          <div key={type} className={`bg-white rounded-lg p-2 border ${colors.border}`}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
                              <span className="text-xs font-medium text-gray-600 truncate">
                                {language === 'pt' ? config.labelPt : config.label}
                              </span>
                            </div>
                            <p className={`text-lg font-bold ${colors.text}`}>{jobs.length}</p>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">{hours.toFixed(1)}h</span>
                              <span className="text-xs font-medium text-gray-600">€{payment.toFixed(0)}</span>
                            </div>
                            <p className="text-[10px] text-gray-400">{config.rate}</p>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Total summary */}
                    <div className="grid grid-cols-4 gap-2 pt-3 border-t border-gray-200">
                      <div className="bg-white rounded-lg p-2 border border-gray-200 text-center">
                        <p className="text-xs text-gray-500">{language === 'pt' ? 'Serviços' : 'Services'}</p>
                        <p className="text-xl font-bold text-gray-700">{filteredCompletedJobs.length}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2 border border-blue-200 text-center">
                        <p className="text-xs text-blue-600">{language === 'pt' ? 'Horas Reais' : 'Real Hours'}</p>
                        <p className="text-xl font-bold text-blue-600">{realHoursWorked.toFixed(1)}h</p>
                        <p className="text-[9px] text-blue-400">{language === 'pt' ? 'Timer/Manual' : 'Timer/Manual'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border border-gray-200 text-center">
                        <p className="text-xs text-gray-500">{language === 'pt' ? 'Horas Est.' : 'Est. Hours'}</p>
                        <p className="text-xl font-bold text-gray-400">{totalHoursWeek.toFixed(1)}h</p>
                        <p className="text-[9px] text-gray-400">{language === 'pt' ? 'Baseado serviço' : 'Service based'}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-200 text-center">
                        <p className="text-xs text-emerald-600">{language === 'pt' ? 'A Pagar' : 'To Pay'}</p>
                        <p className="text-xl font-bold text-emerald-600">€{(realHoursWorked > 0 ? realPayment : totalPaymentWeek).toFixed(2)}</p>
                        <p className="text-[9px] text-emerald-400">{realHoursWorked > 0 ? (language === 'pt' ? 'Horas reais' : 'Real hours') : (language === 'pt' ? 'Estimado' : 'Estimated')}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* All Time Stats */}
                  <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-gray-700">{completedJobs.length}</p>
                      <p className="text-[10px] text-gray-500">{language === 'pt' ? 'Total' : 'Total'}</p>
                    </div>
                    {Object.entries(serviceConfig).map(([type, config]) => {
                      const colors = colorClasses[config.color];
                      return (
                        <div key={type} className={`${colors.bg} rounded-lg p-2 text-center`}>
                          <p className={`text-lg font-bold ${colors.text}`}>{allTimeByType[type].length}</p>
                          <p className={`text-[10px] ${colors.text}/70`}>{language === 'pt' ? config.labelPt : config.label}</p>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* IBAN */}
                  <div className="mt-2 flex items-center justify-between p-2 bg-yellow-50 rounded-lg border border-yellow-100">
                    <span className="text-xs text-yellow-700 font-medium">IBAN:</span>
                    <span className="text-sm font-bold text-yellow-800">{member.iban || '-'}</span>
                  </div>
                  
                  {/* Pending Jobs */}
                  {memberAppointments.filter(a => a.status === 'scheduled').length > 0 && (
                    <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-100">
                      <span className="text-xs text-orange-700">
                        <Briefcase size={12} className="inline mr-1" />
                        {memberAppointments.filter(a => a.status === 'scheduled').length} {language === 'pt' ? 'trabalhos agendados' : 'scheduled jobs'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unassigned Jobs */}
      <div className="bg-white rounded-xl border border-gray-200 mb-8">
        <div className="p-4 border-b bg-yellow-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <AlertCircle size={20} className="text-yellow-600" />
            {language === 'pt' ? 'Trabalhos Sem Atribuição' : 'Unassigned Jobs'} ({unassignedJobs.length})
          </h3>
        </div>
        
        {unassignedJobs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Check size={32} className="mx-auto mb-2 text-green-500" />
            <p>{language === 'pt' ? 'Todos os trabalhos estão atribuídos!' : 'All jobs are assigned!'}</p>
          </div>
        ) : (
          <div className="divide-y">
            {unassignedJobs.map((job) => (
              <div key={`${job.type}-${job.id}`} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                      {language === 'pt' ? 'Agendamento' : 'Appointment'}
                    </span>
                    <span className="font-medium text-gray-900">{job.name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Briefcase size={14} />
                      {job.service_type}
                    </span>
                    {job.preferred_date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {job.preferred_date}
                      </span>
                    )}
                    {job.preferred_time && (
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {job.preferred_time}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => openAssignModal(job, job.type)}
                  className="flex items-center gap-2 px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
                  data-testid={`assign-job-${job.id}`}
                >
                  <UserCheck size={16} />
                  {language === 'pt' ? 'Atribuir' : 'Assign'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assigned Jobs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b bg-green-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <UserCheck size={20} className="text-green-600" />
            {language === 'pt' ? 'Trabalhos Atribuídos' : 'Assigned Jobs'} ({assignedJobs.length})
          </h3>
        </div>
        
        {assignedJobs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Briefcase size={32} className="mx-auto mb-2 text-gray-300" />
            <p>{language === 'pt' ? 'Nenhum trabalho atribuído ainda' : 'No jobs assigned yet'}</p>
          </div>
        ) : (
          <div className="divide-y">
            {assignedJobs.map((job) => (
              <div key={`${job.type}-${job.id}`} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                      {language === 'pt' ? 'Agendamento' : 'Appointment'}
                    </span>
                    <span className="font-medium text-gray-900">{job.name}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700' :
                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {job.status === 'completed' ? (language === 'pt' ? 'Concluído' : 'Completed') :
                       job.status === 'in_progress' ? (language === 'pt' ? 'Em Progresso' : 'In Progress') :
                       (language === 'pt' ? 'Agendado' : 'Scheduled')}
                    </span>
                  </div>
                </div>
                {/* Staff Assignment Section */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm text-gray-600 font-medium">
                    {language === 'pt' ? 'Staff:' : 'Staff:'}
                  </span>
                  {getJobStaffNames(job).map((staffName, idx) => {
                    const staffId = job.assigned_staff_ids?.[idx] || job.assigned_staff_id;
                    const isLeader = job.team_leader_id === staffId;
                    return (
                      <div key={idx} className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                        isLeader ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' : 'bg-primary/10 text-primary'
                      }`}>
                        {isLeader && <Star size={14} className="text-amber-500" />}
                        <User size={14} />
                        {staffName}
                        {isLeader && (
                          <span className="text-xs ml-1 font-bold">
                            {language === 'pt' ? '(Líder)' : '(Leader)'}
                          </span>
                        )}
                        {!isLeader && getJobStaffNames(job).length > 1 && (
                          <button
                            onClick={() => handleSetTeamLeader(job, staffId)}
                            className="ml-1 hover:bg-amber-100 hover:text-amber-600 rounded-full p-0.5 transition-colors"
                            title={language === 'pt' ? 'Definir como Team Leader' : 'Set as Team Leader'}
                          >
                            <Star size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveStaff(job, staffId)}
                          className="ml-1 hover:bg-red-100 hover:text-red-600 rounded-full p-0.5 transition-colors"
                          title={language === 'pt' ? 'Remover' : 'Remove'}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => openAssignModal(job, job.type)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-full transition-colors"
                    title={language === 'pt' ? 'Adicionar mais staff' : 'Add more staff'}
                  >
                    <UserPlus size={14} />
                    {language === 'pt' ? 'Adicionar' : 'Add'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Briefcase size={14} />
                      {job.service_type}
                    </span>
                    {job.preferred_date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {job.preferred_date}
                      </span>
                    )}
                    {(job.address || job.eircode) && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {job.eircode || job.address?.slice(0, 30)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => openInstructionsModal(job, job.type)}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    data-testid={`instructions-${job.id}`}
                  >
                    <Send size={14} />
                    {job.staff_instructions 
                      ? (language === 'pt' ? 'Editar Instruções' : 'Edit Instructions')
                      : (language === 'pt' ? 'Adicionar Instruções' : 'Add Instructions')}
                  </button>
                </div>
                {job.staff_instructions && (
                  <div className="mt-2 p-2 bg-primary/5 rounded-lg text-sm text-gray-600 border-l-2 border-primary">
                    <span className="font-medium text-primary">
                      {language === 'pt' ? 'Instruções: ' : 'Instructions: '}
                    </span>
                    {job.staff_instructions}
                  </div>
                )}
                {/* Staff Notes/Observations - from Staff Portal */}
                {job.staff_notes && (
                  <div className="mt-2 p-2 bg-amber-50 rounded-lg text-sm text-gray-700 border-l-2 border-amber-400">
                    <div className="flex items-center gap-1 mb-1">
                      <MessageSquare size={14} className="text-amber-600" />
                      <span className="font-medium text-amber-700">
                        {language === 'pt' ? 'Observações do Funcionário:' : 'Staff Notes:'}
                      </span>
                    </div>
                    <p className="text-gray-600">{job.staff_notes}</p>
                    {job.staff_notes_updated_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(job.staff_notes_updated_at).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-IE')}
                      </p>
                    )}
                  </div>
                )}
                {/* Time worked by staff */}
                {job.time_worked_seconds && job.status === 'completed' && (
                  <div className="mt-2 p-2 bg-green-50 rounded-lg text-sm border-l-2 border-green-400">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-green-600" />
                      <span className="font-medium text-green-700">
                        {language === 'pt' ? 'Tempo registado:' : 'Time logged:'}
                      </span>
                      <span className="text-green-600 font-bold">
                        {Math.floor(job.time_worked_seconds / 3600)}h {Math.floor((job.time_worked_seconds % 3600) / 60)}m
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Staff Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {language === 'pt' ? 'Novo Funcionário' : 'New Staff Member'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateStaff} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Staff ID *
                </label>
                <input
                  type="text"
                  value={formData.staff_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, staff_id: e.target.value }))}
                  placeholder="ex: joao123"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  data-testid="staff-id-field"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'pt' ? 'ID único para login' : 'Unique ID for login'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Nome *' : 'Name *'}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={language === 'pt' ? 'Nome completo' : 'Full name'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  data-testid="staff-name-field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="staff-email-field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Telefone' : 'Phone'}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+353 83 123 4567"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="staff-phone-field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Palavra-passe *' : 'Password *'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="********"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  minLength={6}
                  data-testid="staff-password-field"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IBAN
                  </label>
                  <input
                    type="text"
                    value={formData.iban}
                    onChange={(e) => setFormData(prev => ({ ...prev, iban: e.target.value.toUpperCase() }))}
                    placeholder="IE29AIBK..."
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    data-testid="staff-iban-field"
                  />
                </div>
              </div>

              {/* Payment Rates Section */}
              <div className="border-t pt-4 mt-2">
                <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  💰 {language === 'pt' ? 'Taxas de Pagamento (€/hora)' : 'Payment Rates (€/hour)'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {language === 'pt' ? 'Limpeza Regular' : 'Regular Cleaning'}
                    </label>
                    <input
                      type="number"
                      step="0.50"
                      min="0"
                      value={formData.regular_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, regular_rate: e.target.value }))}
                      placeholder="15.00"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      data-testid="staff-regular-rate-field"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {language === 'pt' ? 'Deep Clean / Fim Arrend.' : 'Deep Clean / End Tenancy'}
                    </label>
                    <input
                      type="number"
                      step="0.50"
                      min="0"
                      value={formData.deep_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, deep_rate: e.target.value }))}
                      placeholder="17.50"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      data-testid="staff-deep-rate-field"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  data-testid="submit-create-staff"
                >
                  {language === 'pt' ? 'Criar' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {language === 'pt' ? 'Editar Funcionário' : 'Edit Staff'}
              </h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleUpdateStaff} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff ID</label>
                <input
                  type="text"
                  value={formData.staff_id}
                  disabled
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Nome' : 'Name'}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Telefone' : 'Phone'}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Nova Palavra-passe' : 'New Password'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={language === 'pt' ? 'Deixe vazio para manter' : 'Leave empty to keep current'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                <input
                  type="text"
                  value={formData.iban || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, iban: e.target.value.toUpperCase() }))}
                  placeholder="IE29AIBK..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Payment Rates Section */}
              <div className="border-t pt-4 mt-2">
                <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  💰 {language === 'pt' ? 'Taxas de Pagamento (€/hora)' : 'Payment Rates (€/hour)'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {language === 'pt' ? 'Limpeza Regular' : 'Regular Cleaning'}
                    </label>
                    <input
                      type="number"
                      step="0.50"
                      min="0"
                      value={formData.regular_rate || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, regular_rate: e.target.value }))}
                      placeholder="15.00"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {language === 'pt' ? 'Deep Clean / Fim Arrend.' : 'Deep Clean / End Tenancy'}
                    </label>
                    <input
                      type="number"
                      step="0.50"
                      min="0"
                      value={formData.deep_rate || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, deep_rate: e.target.value }))}
                      placeholder="17.50"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {language === 'pt' ? 'Salvar' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Job Modal */}
      {showAssignModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {language === 'pt' ? 'Atribuir Trabalho' : 'Assign Job'}
              </h3>
              <button onClick={() => setShowAssignModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{selectedJob.name}</p>
              <p className="text-sm text-gray-600">{selectedJob.service_type}</p>
              {selectedJob.preferred_date && (
                <p className="text-sm text-gray-500 mt-1">
                  {selectedJob.preferred_date} {selectedJob.preferred_time && `às ${selectedJob.preferred_time}`}
                </p>
              )}
              {/* Show currently assigned staff */}
              {getJobStaffNames(selectedJob).length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    {language === 'pt' ? 'Staff já atribuído:' : 'Already assigned:'}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {getJobStaffNames(selectedJob).map((name, idx) => (
                      <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'pt' ? 'Selecionar Funcionário para Adicionar' : 'Select Staff to Add'}
              </label>
              {staff.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  {language === 'pt' ? 'Nenhum funcionário disponível' : 'No staff available'}
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {staff.map((member) => {
                    const alreadyAssigned = isStaffAssigned(selectedJob, member.id);
                    return (
                      <button
                        key={member.id}
                        onClick={() => !alreadyAssigned && setSelectedStaff(member)}
                        disabled={alreadyAssigned}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                          alreadyAssigned 
                            ? 'border-green-200 bg-green-50 cursor-not-allowed opacity-60' 
                            : selectedStaff?.id === member.id 
                              ? 'border-primary bg-primary/5' 
                              : 'border-gray-200 hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{member.name}</p>
                            <p className="text-sm text-gray-500">{member.staff_id}</p>
                          </div>
                          {alreadyAssigned && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              {language === 'pt' ? 'Já atribuído' : 'Assigned'}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowAssignModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={handleAssignJob}
                disabled={!selectedStaff || isStaffAssigned(selectedJob, selectedStaff?.id)}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {language === 'pt' ? 'Adicionar Staff' : 'Add Staff'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions Modal */}
      {showInstructionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {language === 'pt' ? 'Instruções para o Funcionário' : 'Instructions for Staff'}
              </h3>
              <button onClick={() => setShowInstructionsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'pt' 
                  ? 'O que o funcionário deve fazer neste serviço?'
                  : 'What should the staff do for this service?'}
              </label>
              <textarea
                value={instructionsForm.instructions}
                onChange={(e) => setInstructionsForm(prev => ({ ...prev, instructions: e.target.value }))}
                placeholder={language === 'pt' 
                  ? 'Ex: Atenção às janelas da sala. Usar produtos debaixo da pia...'
                  : 'Ex: Pay attention to living room windows. Use products under the sink...'}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={5}
                data-testid="instructions-textarea"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowInstructionsModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={handleSaveInstructions}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                data-testid="save-instructions-btn"
              >
                {language === 'pt' ? 'Salvar' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStaffManagement;
